/* eslint-disable @typescript-eslint/no-shadow */
import { CacheKeys } from '@/utils/cacheKeys';
import AdditionalInfo from '../partials/AdditionalInfo';
import AgentCapabilitiesForm from '../partials/AgentCapabilies';
import BusinessDetailsForm from '../partials/BusinessDetailsForm';
import FAQsForm from '../partials/FAQsForm';
import InternalProcessForm from '../partials/InternalProcessForm';
import LocationDetailsForm from '../partials/LocationDetailsForm';
import OperatorDetailsForm from '../partials/OperatorDetailsForm';
import OfficeOperationsForm from '../partials/OfficeOperationsForm';
import TelephonyDetailForm from '../partials/TelephonyDetailForm';
import CustomerSentimentsForm from '../partials/CustomerSentimentsForm';
import IntegrationDetailForm from '../partials/IntegrationDetailForm';
import { STEP_FIELDS, STEP_REQUIRED_FIELDS } from './constants';
import { setCookie } from '@/utils/helperFunctions';
import { isUsPhoneNumber } from '@/utils/isUsPhoneNumber';

export const ONBOARDING_PROGRESS_CHANGED = 'onboarding_progress_changed';
export const ONBOARDING_COMPLETED = 'onboarding_completed';

const INTEGRATION_META_KEYS = [
  'valid',
  'valid_creds',
  'redirect_uri',
  'post_configurations',
  'configuration',
  'validation_message'
] as const;

export const omitIntegrationValidationMeta = (configuration: Record<string, unknown> = {}) => {
  const credentials = { ...configuration };
  delete credentials.valid;
  delete credentials.valid_creds;
  delete credentials.redirect_uri;
  delete credentials.validation_message;
  return credentials;
};

const OAUTH_STORED_TOKEN_KEYS = [
  'access_token',
  'refresh_token',
  'oauth_token',
  'oauth_token_secret',
  'access_token_expires_in',
  'access_token_expires_at',
  'refresh_token_last_used_at',
  'jsessionid'
] as const;

export const stripOAuthTokensFromConfiguration = (configuration: Record<string, unknown> = {}) => {
  const credentials = { ...configuration };
  OAUTH_STORED_TOKEN_KEYS.forEach((key) => {
    delete credentials[key];
  });
  return credentials;
};

/** Flat credential keys from nested or legacy-flat integration_configuration. */
export const getIntegrationCredentials = (raw: Record<string, unknown> = {}): Record<string, unknown> => {
  const trimCredentialValue = (value: unknown): unknown => {
    if (typeof value === 'string') return value.trim();
    if (Array.isArray(value)) {
      return value.map((item) => (typeof item === 'string' ? item.trim() : item));
    }
    return value;
  };

  const trimCredentials = (credentials: Record<string, unknown>) =>
    Object.fromEntries(Object.entries(credentials).map(([key, value]) => [key, trimCredentialValue(value)]));

  const nested = raw.configuration;
  if (nested && typeof nested === 'object' && !Array.isArray(nested) && Object.keys(nested as object).length > 0) {
    return trimCredentials({ ...(nested as Record<string, unknown>) });
  }

  const credentials: Record<string, unknown> = {};
  Object.entries(raw).forEach(([key, value]) => {
    if ((INTEGRATION_META_KEYS as readonly string[]).includes(key)) return;
    credentials[key] = value;
  });
  return trimCredentials(credentials);
};

/** Normalize to nested shape: credentials only under configuration. */
export const normalizeIntegrationConfiguration = (
  raw: Record<string, unknown> = {},
  overrides: {
    configuration?: Record<string, unknown>;
    redirect_uri?: string;
    valid?: boolean;
    valid_creds?: boolean;
    validation_message?: string | null;
  } = {}
): Record<string, unknown> => {
  const configuration = overrides.configuration ?? getIntegrationCredentials(raw);
  const normalized: Record<string, unknown> = { configuration };

  const redirectUri = overrides.redirect_uri ?? raw.redirect_uri;
  if (typeof redirectUri === 'string' && redirectUri.length > 0) {
    normalized.redirect_uri = redirectUri;
  }

  if (raw.post_configurations !== undefined) {
    normalized.post_configurations = raw.post_configurations;
  }

  const valid = overrides.valid ?? raw.valid;
  const validCreds = overrides.valid_creds ?? raw.valid_creds;
  if (valid !== undefined) normalized.valid = valid;
  if (validCreds !== undefined) normalized.valid_creds = validCreds;

  const validationMessage =
    overrides.validation_message !== undefined ? overrides.validation_message : raw.validation_message;
  if (valid === true) {
    // Clear stale failure messages when credentials are valid
  } else if (typeof validationMessage === 'string' && validationMessage.length > 0) {
    normalized.validation_message = validationMessage;
  }

  return normalized;
};

export const STEPS = (isPublicRoute?: boolean) => [
  ...(!isPublicRoute
    ? [{ label: 'Business Profile', component: BusinessDetailsForm, helperText: 'Tell us about your business' }]
    : []),
  {
    label: 'Office Operations',
    component: OfficeOperationsForm,
    helperText: 'How does your front desk really run?'
  },
  { label: 'Business Locations', component: LocationDetailsForm, helperText: 'Where do you operate?' },
  {
    label: 'Member Details',
    component: OperatorDetailsForm,
    helperText: 'When are you open?'
  },
  { label: 'Agent Capabilities', component: AgentCapabilitiesForm, helperText: 'How should your agent behave?' },
  { label: 'Integration Details', component: IntegrationDetailForm, helperText: 'Creds of your Integration' },
  { label: 'Knowledge Base', component: FAQsForm, helperText: 'What should your agent know?' },
  {
    label: 'Customer Sentiments',
    component: CustomerSentimentsForm,
    helperText: 'What customers say about your business?'
  },
  { label: 'Telephony Details', component: TelephonyDetailForm, helperText: 'How Your Phone system connect?' },
  {
    label: 'Additional Info',
    component: AdditionalInfo,
    helperText: 'Anything else to add?'
  },
  ...(!isPublicRoute
    ? [{ label: 'Internal Process', component: InternalProcessForm, helperText: 'For Office Use Only' }]
    : [])
];

export const getNestedValue = (obj: any, path: string) => {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
};

export const getAbsIndex = (index: number, isPublicRoute?: boolean) => (isPublicRoute ? index + 1 : index);

export const getIntegrationStepIndex = (isPublicRoute?: boolean) =>
  STEPS(isPublicRoute).findIndex((step) => step.component === IntegrationDetailForm);

export const stepHasError = (stepIndex: number, values: any, errors: any, isPublicRoute?: boolean) => {
  const fields = getStepFields(stepIndex, values, isPublicRoute);

  return fields.some((field) => {
    // Check direct match or nested property access
    if (getNestedValue(errors, field)) return true;

    // Handle nested fields like address.city
    const parts = field.split('.');
    if (parts.length > 1) {
      const topLevelError = errors[parts[0]];
      if (topLevelError) {
        // If it's an array (like operator_details) or object with any error
        return true;
      }
    }
    return false;
  });
};

export const getStepProgress = (
  stepIndex: number,
  values: any,
  isPublicRoute?: boolean,
  availableIntegrations?: any[]
) => {
  const absIndex = getAbsIndex(stepIndex, isPublicRoute);
  let fields = [...(STEP_FIELDS[absIndex] || [])];

  const steps = STEPS(isPublicRoute);

  if (!steps[stepIndex]) {
    return { filled: 0, total: 1, progress: 0 };
  }

  // Customer Sentiments
  if (absIndex === 7) {
    const keywords = values?.custom_keywords || [];
    const filled = Array.isArray(keywords) && keywords.length > 0 ? 1 : 0;
    return { filled, total: 1, progress: filled ? 100 : 0 };
  }

  // =========================
  // OFFICE OPERATIONS
  // =========================
  if (absIndex === 1 && fields.includes('office_operations_files_attributes')) {
    const answers = values?.office_operations_files_attributes || [];
    const total = answers.length || 1;
    const filled = answers.filter((a: any) => a?.transcript?.trim()).length;
    return { filled, total, progress: Math.round((filled / total) * 100) };
  }

  // =========================
  // LOCATION STEP: Physical vs Remote
  // =========================
  if (absIndex === 2) {
    const isPhysical = values?.location_details?.physical_location_exists;

    if (!isPhysical) {
      fields = fields.filter(
        (f) =>
          ![
            'location_details.street',
            'location_details.address2',
            'location_details.city',
            'location_details.zip',
            'location_details.country',
            'location_details.state'
          ].includes(f)
      );
    }
  }

  // =========================
  // AGENT CAPABILITIES: Industry filter
  // =========================
  if (absIndex === 4) {
    const industry = values?.industry;

    if (industry !== 'healthcare') {
      fields = fields.filter((f) => f !== 'appointment_booking_enable');
    }

    if (industry !== 'restaurant') {
      fields = fields.filter((f) => f !== 'order_booking_enable');
    }
  }

  // =========================
  // DYNAMIC FIELDS
  // =========================
  fields = [...new Set([...fields, ...getStepFields(stepIndex, values, isPublicRoute, availableIntegrations)])];

  let filled = 0;

  fields.forEach((field) => {
    // =========================
    // STEP 3: Member Details
    // =========================
    if (absIndex === 3) {
      const operators = values?.operator_details || [];

      if (!operators.length) return;

      // Handle the base field 'operator_details'
      if (field === 'operator_details') {
        filled++;
        return;
      }

      const key = field.split('.')[1];

      let isFilled = false;

      // name / email / role / phone / main_line_number / extension / sip_uri / sms_consent
      if (['name', 'email', 'role', 'phone', 'main_line_number', 'extension', 'sip_uri', 'sms_consent'].includes(key)) {
        isFilled = operators.some((op: any) => {
          const val = op?.[key];
          if (key === 'sms_consent') {
            return isUsPhoneNumber(op?.phone) ? val === true : true;
          }
          return typeof val === 'string' ? val.trim() !== '' : !!val;
        });
      }

      // working hours → ANY day open true
      if (key === 'working_hours') {
        isFilled = operators.some((op: any) => {
          const wh = op?.working_hours;

          if (!wh || typeof wh !== 'object') return false;

          return Object.values(wh).some((day: any) => day?.open === true);
        });
      }

      if (isFilled) filled++;

      return;
    }

    // =========================
    // STEP 4: Toggles — only count if CHECKED and ALL internal fields are complete
    // =========================
    if (absIndex === 4) {
      const isEnabled = values?.[field] === true;

      // If NOT enabled, it counts as 0 (as requested: "if i deselect... i want to decrese")
      if (!isEnabled) {
        return;
      }

      // If ENABLED, check mandatory sub-fields
      let isConfigured = false;

      switch (field) {
        case 'emergency_call_handling_enable': {
          const details = values?.emergency_call_handling_details;
          const mode = details?.mode || 'message';
          isConfigured =
            mode === 'message' ? !!details?.message?.trim() : !!(details?.number || details?.phone_number)?.trim();
          break;
        }
        case 'spam_call_termination_enable': {
          const patterns = values?.spam_call_termination_details?.patterns || [];
          isConfigured = patterns.some((p: any) => p?.value?.trim());
          break;
        }
        case 'send_sms_to_caller_enable': {
          isConfigured = !!values?.send_sms_to_caller_details?.message_content?.trim();
          break;
        }
        case 'appointment_booking_enable': {
          const details = values?.appointment_booking_details || [];
          isConfigured =
            details.length > 0 &&
            details.every(
              (d: any) =>
                d?.provider_name?.trim() &&
                (d?.procedures || []).some((p: any) => p?.trim()) &&
                d?.appointment_type &&
                d?.appointment_duration
            );
          break;
        }
        case 'appointment_scheduling_enable': {
          const details = values?.appointment_scheduling_details || {};
          isConfigured = !!details?.cancellation_rules?.trim() && !!details?.rescheduling_rules?.trim();
          break;
        }
        case 'order_booking_enable': {
          const type = values?.order_booking_details?.order_type || {};
          isConfigured = type.pickup === true || type.delivery === true;
          break;
        }
        case 'transfer_call_enable': {
          const detailsList = Array.isArray(values?.transfer_call_details)
            ? values.transfer_call_details
            : values?.transfer_call_details
              ? [values.transfer_call_details]
              : [];
          if (!detailsList.length) {
            isConfigured = false;
            break;
          }
          isConfigured = detailsList.every(
            (details: {
              transfer_mode?: string;
              transfer_type?: string;
              sources?: { target_id?: unknown; target_type?: unknown; target_value?: unknown }[];
            }) => {
              const sources = Array.isArray(details?.sources) ? details.sources : [];
              const transferType = details?.transfer_type || 'operator';
              const sourcesComplete = sources.every(
                (source) =>
                  source?.target_id !== undefined &&
                  source?.target_id !== null &&
                  source?.target_id !== '' &&
                  Boolean(source?.target_type) &&
                  Boolean(source?.target_value)
              );
              if (!details?.transfer_mode || !sourcesComplete) return false;
              if (transferType === 'group') return sources.length >= 2;
              return sources.length >= 1;
            }
          );
          break;
        }
        default:
          isConfigured = true; // Simple toggles without sub-fields (Note taking, Post-call SMS/Email)
      }

      if (isConfigured) filled++;
      return;
    }

    // =========================
    // Knowledge Base: FAQ and documents
    // =========================
    if (absIndex === 6) {
      if (field === 'faq') {
        const faqs = values?.faq || [];

        if (!faqs.length) return;

        const answered = faqs.filter((f: any) => f?.question?.trim() && f?.answer?.trim()).length;

        filled += answered / faqs.length;
        return;
      }

      if (field === 'knowledgebase_files_attributes') {
        const docs = values?.knowledgebase_files_attributes || [];

        if (!docs.length) return;

        const valid = docs.filter((d: any) => d?.file && (typeof d.file === 'string' || d.file instanceof File)).length;

        filled += valid / docs.length;
        return;
      }
    }

    // =========================
    // Additional Info
    // =========================
    if (absIndex === 9) {
      if (field !== 'additional_note') return; // Only process once for the whole step or use a different logic

      const notes = values?.additional_note;
      const docs = values?.additional_documents_attributes || [];
      const confirmed = values?.confirmation;

      let local = 0;

      if (notes?.trim()) local += 0.3;

      // Count valid documents
      const validDocs = docs.filter(
        (d: any) => d?.file && (typeof d.file === 'string' || d.file instanceof File)
      ).length;
      if (validDocs > 0) local += 0.3;

      if (isPublicRoute && confirmed) local += 0.4;

      const max = isPublicRoute ? 1 : 0.6;
      filled += (local / max) * fields.length; // Scale to match total fields for this step

      return;
    }

    // =========================
    // Internal process
    // =========================
    if (absIndex === 10) {
      if (field !== 'business_plan_id') return; // Only process once

      let local = 0;

      if (values?.business_plan_id) local += 0.3;
      if (values?.voice_engine_id) local += 0.3;
      if (values?.ticket_reference) local += 0.2;
      if (values?.coupon_code) local += 0.05;
      if (values?.stripe_customer_id) local += 0.05;

      const hasNote = (values?.interoffice_notes || []).some((n: any) => n?.note?.trim());

      if (hasNote) local += 0.1;

      filled += local * fields.length;
      return;
    }

    // =========================
    // NORMAL FIELDS
    // =========================
    const value = getNestedValue(values, field);

    // business hours check (specific logic for Location Details)
    if (field === 'business_hours' && absIndex === 2) {
      if (value && typeof value === 'object') {
        const isOpen = Object.values(value).some((day: any) => day?.open === true);

        if (isOpen) filled++;
      }
      return;
    }

    if (Array.isArray(value)) {
      if (value.length > 0 && value[0]) filled++;
    } else if (typeof value === 'object' && value !== null) {
      if (Object.keys(value).length > 0) filled++;
    } else if (typeof value === 'string') {
      if (value.trim() !== '') filled++;
    } else if (typeof value === 'boolean') {
      filled++; // Boolean values (true/false) count as filled
    } else if (value !== undefined && value !== null && value !== '') {
      filled++;
    }
  });

  const total = fields.length || 1;
  const progress = Math.round((filled / total) * 100);

  return { filled, total, progress };
};

export const getOverallFormProgress = (values: any, isPublicRoute?: boolean, availableIntegrations?: any[]) => {
  const steps = STEPS(isPublicRoute);

  let totalFields = 0;
  let filledFields = 0;
  const stepProgresses = steps.map((_, stepIndex) => {
    const absIndex = getAbsIndex(stepIndex, isPublicRoute);
    // Skip Internal Process for overall progress percentage if preferred,
    // but here we just ensure we use correct indices.
    if (absIndex === 10) return;

    const { filled, total, progress } = getStepProgress(stepIndex, values, isPublicRoute, availableIntegrations);

    // Skip Additional Info from overall progress percentage calculation
    if (absIndex !== 9) {
      totalFields += total;
      filledFields += filled;
    }
    return progress;
  });

  if (!totalFields) {
    setCookie(CacheKeys.onboardingFormProgress, 0);
    return { filled: 0, total: 0, percentage: 0, stepProgresses };
  }

  const percentage = Math.round((filledFields / totalFields) * 100);

  setCookie(CacheKeys.onboardingFormProgress, percentage);

  return {
    filled: Math.round(filledFields),
    total: totalFields,
    percentage,
    stepProgresses
  };
};

export const getStepFields = (
  stepIndex: number,
  values: any,
  isPublicRoute?: boolean,
  availableIntegrations?: any[]
): string[] => {
  const absIndex = getAbsIndex(stepIndex, isPublicRoute);

  if (absIndex === 2) {
    const isPhysical = values?.location_details?.physical_location_exists;

    const baseFields = ['location_details.count', 'location_details.location_name', 'address.time_zone'];

    if (isPhysical) {
      return [
        ...baseFields,
        'location_details.street',
        'location_details.city',
        'location_details.zip',
        'location_details.country',
        'location_details.state'
      ];
    }

    return baseFields;
  }

  // Integration Details
  if (absIndex === 5) {
    const baseFields = ['available_integration_ids'];
    const selectedIntegrationId = values?.available_integration_ids?.[0];

    // If "No Integration" or nothing selected, just return base
    if (!selectedIntegrationId || selectedIntegrationId === ' ' || selectedIntegrationId === '') {
      return baseFields;
    }

    if (selectedIntegrationId === 'other') {
      return [...baseFields, 'other_integration_name', 'integration_configuration.other_credential'];
    }

    // Use the passed integrations list as the source of truth for required fields
    const selectedIntegration = availableIntegrations?.find((i: any) => String(i.id) === String(selectedIntegrationId));
    const requiredKeys = selectedIntegration?.pre_configurations?.map((f: any) => f.key) || [];

    const nestedConfig = values?.integration_configuration?.configuration || {};
    const presentKeys = Object.keys(nestedConfig).filter((key) => {
      const val = nestedConfig[key];
      return val !== undefined && val !== null && val !== '';
    });

    const dynamicKeys = [...new Set([...requiredKeys, ...presentKeys])];

    return [...baseFields, ...dynamicKeys.map((key: string) => `integration_configuration.configuration.${key}`)];
  }

  return STEP_REQUIRED_FIELDS[absIndex] || [];
};
